import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppStore } from '../../store/applicationState';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import { StylesTable } from '../Stylestable';
import Pagination from '@mui/material/Pagination';
import TextField from '@mui/material/TextField';
import { IProduct } from '../../store/products/types';
import { EnhancedTableHead } from '../ui';
import { Order, stableSort } from '../../utils/stableSort';
import { GetScholl, GetSchollCsv } from '../../store/scholl/actions';
import Button from '@mui/material/Button';
import { BasicDatePicker, SelectOutline, SelectSearchReq } from '../ui';
import { useSearchProducts } from '../../hooks/useSearchProducts';
import { IDataOpt } from '../ui/SelectSearchReq';
import moment from 'moment';
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { IScholl } from "../../store/scholl/types";
import { EditRow } from "./EditRow";
import CircularProgress from '@mui/material/CircularProgress';

interface Column {
  id: 'user_id' | 'fio' | 'ukd' | 'UkdDateStart' | 'UkdDateEnd' | 'ProductVersionName'
  | 'fioCertificate' | 'changedFioCertificate' | 'DateTest' | 'ProgramTypeint' | 'Printed' | 'DataNP';
  label: string;
  sort?: boolean;
  align?: 'right' | 'left' | 'center';
  width?: string;
  roles?: number[];
  minWidth?: string;
}

const columns: Column[] = [
  { id: 'user_id', label: 'Userid', align: 'center', sort: true, width: '5%' },
  { id: 'fio', label: 'ПІБ', align: 'center', sort: true, width: '7%' },
  { id: 'ukd', label: 'Код УКД', align: 'center', sort: true, width: '10%', minWidth: '160px' },
  { id: 'UkdDateStart', label: 'Дата старту УКД', align: 'center', sort: true, width: '5%' },
  { id: 'UkdDateEnd', label: 'Дата закінчення УКД', align: 'center', sort: true, width: '5%' },
  { id: 'ProductVersionName', label: 'Назва версії', align: 'center', sort: true, width: '10%' },
  { id: 'fioCertificate', label: 'ПІБ для сертифіката', align: 'center', sort: true, width: '7%' },
  { id: 'changedFioCertificate', label: 'Змінений ПІБ для сертифіката', align: 'center', sort: true, width: '7%' },
  { id: 'DataNP', label: 'Дані нової пошти', align: 'center', sort: true, width: '7%' },
  { id: 'DateTest', label: 'Дата здачі підсумкового тесту', align: 'center', sort: true, width: '5%' },
  { id: 'ProgramTypeint', label: 'Вид сертифіката', align: 'center', sort: true, width: '5%' },
  { id: 'Printed', label: 'Потрібен друк. сертифікат', align: 'center', sort: true, width: '5%' },
];

const initialFilters = {
  userId: '',
  fio: '',
  ukd: '',
  ukdDateStart: null as Date | null,
  ukdDateEnd: null as Date | null,
  productVersionName: '',
  fioCertificate: '',
  dateTest: null as Date | null,
  programType: '',
  printed: '',
  productMain: null as IDataOpt | null,
  productId: '',
};

type PropsType = {};

export const ListSchool: React.FC<PropsType> = () => {
  const [tempFilters, setTempFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof IProduct>('absnum');
  const [editData, setEditData] = useState<IScholl | null>(null);
  const { Scholl } = useSelector((store: AppStore) => store);
  const [isNPDataForm, setIsNPDataForm] = useState<boolean>(false);

  const dispatch = useDispatch();
  const classesTable = StylesTable();
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IProduct) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const { subOptions, mainOptions, selectedMainProduct, handleSearch: handleSearchProduct } = useSearchProducts();

  const handleChangePage = (event: unknown, newPage: number) => {
    if (!Scholl.data) return null;
    if (Scholl.data.pages >= newPage) {
      dispatch(
        GetScholl.request({
          data: {
            ...buildRequestData(appliedFilters),
            page: newPage,
          }
        })
      );
    }
  };

  const clearFilters = () => {
    if (Scholl.loading) return;

    setTempFilters(initialFilters)
    setAppliedFilters(initialFilters)
    selectedMainProduct(null);

    dispatch(GetScholl.request({ data: {} }));
  };


  const exportFile = () => {
    dispatch(
      GetSchollCsv.request({
        data: buildRequestData(appliedFilters),
      })
    );
  };

  const handleSearch = () => {
    if (Scholl.loading) return;

    setAppliedFilters({ ...tempFilters });

    dispatch(
      GetScholl.request({
        data: buildRequestData(tempFilters),
      })
    );
  };

  const handleCancel = () => {
    setEditData(null);
    setIsNPDataForm(false);
  };

  const calculateDiff = (dateEnd?: string | undefined, countOfAvailableDays: number = 0) => {
    if (!dateEnd) return false;
    const dateEndMoment = moment(dateEnd);
    const dateNow = moment();
    const diff = dateNow.diff(dateEndMoment, 'days');
    return diff <= countOfAvailableDays;
  };

  const defineCountOfDaysAndDateEnd = (row: IScholl) => {
    const result = {
      countOfAvailableDays: 60,
      dateEnd: row.dateEnd
    } as {
      countOfAvailableDays: number,
      dateEnd: string
    };

    if (row.appId === 2511 || row.version_product === '50015') {
      result.countOfAvailableDays = 16
    } else if (row.version_product === '50068') {
      const dateEndPlusThreeMonth = moment(row.dateStart).add(3, 'months');
      result.countOfAvailableDays = dateEndPlusThreeMonth.diff(row.dateStart, 'days');
      result.dateEnd = row.dateStart
    } else if (row.version_product === '50046' || row.version_product === '50003') {
      const dateEndPlusThreeMonth = moment(row.dateStart).add(4, 'months');
      result.countOfAvailableDays = dateEndPlusThreeMonth.diff(row.dateStart, 'days');
      result.dateEnd = row.dateStart
    }

    return result
  };

  const isAvailableEditMode = (row: IScholl) => {
    const { countOfAvailableDays, dateEnd } = defineCountOfDaysAndDateEnd(row);
    return calculateDiff(dateEnd, countOfAvailableDays);
  }
  if (editData) {
    return <EditRow data={editData} handleCancel={handleCancel} isNPDataForm={isNPDataForm} />;
  }

  const buildRequestData = (filters: typeof tempFilters) => ({
    userId: filters.userId,
    fio: filters.fio,
    ukd: filters.ukd,
    ukdDateStart: filters.ukdDateStart,
    ukdDateEnd: filters.ukdDateEnd,
    productVersionName: filters.productVersionName,
    fioCertificate: filters.fioCertificate,
    dateTest: filters.dateTest,
    programType: filters.programType,
    printed: filters.printed,
    productMainId: filters.productMain ? String(filters.productMain.id) : '',
    productId: filters.productId,
  });

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '89vh',
        overflow: 'hidden',
      }}
    >
      <Box style={{ display: 'flex', flexWrap: 'wrap', marginTop: '5px'  }}>
        <TextField
          style={{ marginRight: '10px' }}
          size="small"
          id="outlined-basic-id"
          label="Userid"
          variant="outlined"
          className={classesTable.searchField}
          value={tempFilters.userId}
          onChange={(e) =>
            setTempFilters((prev) => ({
              ...prev,
              userId: e.target.value,
            }))
          }
        />
        <TextField
          style={{ marginRight: '10px' }}
          size="small"
          id="outlined-basic-id"
          label="ПІБ"
          variant="outlined"
          value={tempFilters.fio}
          className={classesTable.searchField}
          onChange={(e) =>
            setTempFilters((prev) => ({
              ...prev,
              fio: e.target.value,
            }))
          }
        />
        <TextField
          style={{ marginRight: '10px' }}
          size="small"
          id="outlined-basic-id"
          label="ПІБ для сертифіката"
          variant="outlined"
          value={tempFilters.fioCertificate}
          className={classesTable.searchField}
          onChange={(e) => setTempFilters((prev) => ({
            ...prev,
            fioCertificate: e.target.value,
          }))}
        />
        <TextField
          style={{ marginRight: '10px' }}
          size="small"
          id="outlined-basic-id"
          label="УКД"
          variant="outlined"
          value={tempFilters.ukd}
          className={classesTable.searchField}
          onChange={(e) => setTempFilters((prev) => ({
            ...prev,
            ukd: e.target.value,
          }))}
        />
        <div className={classesTable.searchField} style={{ width: '210px' }}>
          <BasicDatePicker
            id="ukdDateStart"
            label="Дата старту"
            value={tempFilters.ukdDateStart}
            onChange={(date) => setTempFilters((prev) => ({
              ...prev,
              ukdDateStart: date,
            }))}
          />
        </div>
        <div className={classesTable.searchField} style={{ width: '210px' }}>
          <BasicDatePicker
            id="dateEnd"
            label="Дата закінчення"
            value={tempFilters.ukdDateEnd}
            onChange={(date) => setTempFilters((prev) => ({
              ...prev,
              ukdDateEnd: date,
            }))} />
        </div>
        <div className={classesTable.searchField} style={{ width: '210px' }}>
          <BasicDatePicker
            id="dateTest"
            label="Дата здачі тесту"
            value={tempFilters.dateTest}
            onChange={(date) => setTempFilters((prev) => ({
              ...prev,
              dateTest: date,
            }))} />
        </div>
        <SelectSearchReq
          data={tempFilters.productMain}
          style={{ marginRight: '10px', width: '210px' }}
          title="Головний продукт"
          className={classesTable.searchField}
          onChange={(data) => {
            selectedMainProduct(data);
            setTempFilters((prev) => ({
              ...prev,
              productMain: data,
              productId: '',
            }));
          }}
          options={mainOptions}
          handleRequest={handleSearchProduct}
        />
        <SelectOutline
          style={{ marginRight: '10px', width: '210px' }}
          id="outlined-basic"
          title="Версія продукту"
          value={tempFilters.productId}
          className={classesTable.searchField}
          options={subOptions}
          handleChange={(value) => setTempFilters((prev) => ({
            ...prev,
            productId: value,
          }))}
        />
        <SelectOutline
          style={{ width: '210px' }}
          className={classesTable.searchField}
          title="Потрібен друкований сертифікат"
          options={[
            { value: '3', label: 'Усі' },
            { value: '0', label: 'Так' },
            { value: '1', label: 'Ні' },
          ]}
          handleChange={(value) => setTempFilters((prev) => ({
            ...prev,
            printed: value,
          }))}
          value={tempFilters.printed}
        />
        <SelectOutline
          style={{ width: '210px' }}
          className={classesTable.searchField}
          title="Вид сертифіката"
          options={[
            { value: '3', label: 'Усі' },
            { value: '1', label: 'Друкований' },
            { value: '0', label: 'Електронний' },
          ]}
          handleChange={(value) => setTempFilters((prev) => ({
            ...prev,
            programType: value,
          }))}
          value={tempFilters.programType}
        />
        <Button variant="contained" color="primary" disabled={Scholl.loading} onClick={handleSearch} style={{ height: '40px' }}>
          Пошук
        </Button>
        <Button variant="contained" color="primary" onClick={clearFilters} style={{ height: '40px', marginLeft: '10px' }}>
          Очистити фільтри
        </Button>
        <Button variant="contained" color="primary" onClick={exportFile} style={{ height: '40px', marginLeft: '10px' }}>
          Вивантажити у XLSX
        </Button>
      </Box>
      {Scholl.loading ?
        <CircularProgress
          style={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-20px', marginLeft: '-20px' }}
        />
        : <>
          <TableContainer
            className={classesTable.container}
            style={{
              marginBottom: "40px",
              overflow: 'auto',
            }}
          >
            <Table className={classesTable.table} stickyHeader aria-label="sticky table" style={{ width: 'auto' }}>
              <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} columns={columns} />
              <TableBody>
                {Scholl && Scholl.data &&
                  stableSort(Scholl.data.scholl, order, orderBy).map((row, i) => {
                    return (
                      <TableRow style={{ maxHeight: 50 }} hover role="checkbox" tabIndex={-1} key={`${i}-${row.userId}`}>
                        <TableCell align="center">
                          {row.userId}
                        </TableCell>
                        <TableCell align="center">
                          {`${row.lname} ${row.fname} ${row.mname}`}
                        </TableCell>
                        <TableCell align="center">
                          {row.ukd}
                        </TableCell>
                        <TableCell align="center" style={{ overflowWrap: 'break-word' }}>
                          {moment(row.dateStart).format('DD-MM-YYYY')}
                        </TableCell>
                        <TableCell align="center" style={{ overflowWrap: 'break-word' }}>
                          {moment(row.dateEnd).format('DD-MM-YYYY')}
                        </TableCell>
                        <TableCell align="center">
                          {row.version_product_name}
                        </TableCell>
                        <TableCell align="center">
                          {`${row.second_name} ${row.first_name} ${row.middle_name}`}
                        </TableCell>
                        <TableCell align="center" style={{ position: 'relative' }}>
                          {isAvailableEditMode(row) &&
                            <IconButton
                              aria-label="edit"
                              onClick={() => setEditData(row)}
                            >
                              <EditIcon
                                fontSize="small"
                              />
                            </IconButton>}
                          <br />
                          {`${row?.changed_second_name || ''} ${row?.changed_first_name || ''} ${row?.changed_middle_name || ''}`}
                          <span style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                            <br />
                            {row.changed_by ? 'Змінив: ' + row.changed_by : ''}
                          </span>
                        </TableCell>
                        <TableCell align="center" style={{ position: 'relative' }}>
                          {(isAvailableEditMode(row) && !row?.no_print) &&
                            <IconButton
                              aria-label="edit"
                              onClick={() => {
                                setEditData(row);
                                setIsNPDataForm(true);
                              }}
                            >
                              <EditIcon
                                fontSize="small"
                              />
                            </IconButton>}
                          <br />
                          <span style={{ fontStyle: 'italic', fontSize: '0.8em' }}>
                            <br />
                          </span>
                        </TableCell>
                        <TableCell align="center">
                          {row.date_test ? moment(row.date_test).format('DD-MM-YYYY') : ''}
                        </TableCell>
                        <TableCell align="center">
                          {row.printed}
                        </TableCell>
                        <TableCell align="center">
                          {row.printed === '0' ? 'Ні' : row.no_print ? 'Ні' : 'Так'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            style={{
              flexShrink: 0,
              padding: "10px 0px",
              background: " rgb(255, 255, 255)"
            }}
          >
            <Pagination
              className={classesTable.pagination}
              count={Scholl && Scholl.data ? Scholl.data.pages : 0}
              page={Scholl && Scholl.data ? Scholl.data.currentPage : 1}
              onChange={handleChangePage}
              style={{
                width: "100%",
                bottom: "2vh",
                right: 0,
                flexShrink: 0,
                padding: "10px 0px",
                position: "absolute",
                marginRight: "0vw",
              }}
            />
          </Box>
        </>}
    </Box>
  );
};
